/**
 * 上报器：令牌桶限流 + 微批队列 + 发送
 *
 * 有意例外（CLAUDE.md「非必要不加 try/catch」）：
 * 上报失败必须静默吞掉。若上报错误再触发上报，一次网络故障就会变成无限递归，
 * 直接把浏览器打满。因此这里用裸 fetch（绕开 axios 响应拦截器）、
 * 不重试、不打 console、不向外抛异常。
 */
import { MONITOR_CONFIG } from '@/monitor/config';
import { buildContext } from '@/monitor/core/context';
import { formatDingtalk } from '@/monitor/formatters/dingtalk';
import type { AggregatedEvent } from '@/types/monitor';

/** 上报地址，由 configureReporter 注入 */
let webhook = '';
/** 总开关：未 configure 前一律不收集，避免监控关闭时队列无限增长 */
let enabled = false;
/** dry-run：只打 console 不发请求，供本地验证使用 */
let dryRun = false;

let queue: AggregatedEvent[] = [];
/** 被限流丢弃的条数，附在下一条发得出去的消息里 */
let suppressed = 0;
/** 本次页面会话已发送的消息条数 */
let sessionReported = 0;

let tokens: number = MONITOR_CONFIG.BUCKET_CAPACITY;
let lastRefill = Date.now();

/** 令牌桶取令牌：容量 10、每 6s 补 1 个 = 10 条/分钟 */
const takeToken = (now: number): boolean => {
  const refill = Math.floor((now - lastRefill) / MONITOR_CONFIG.TOKEN_REFILL_INTERVAL);
  if (refill > 0) {
    tokens = Math.min(MONITOR_CONFIG.BUCKET_CAPACITY, tokens + refill);
    lastRefill += refill * MONITOR_CONFIG.TOKEN_REFILL_INTERVAL;
  }
  if (tokens <= 0) return false;
  tokens -= 1;
  return true;
};

export const configureReporter = (options: { webhook: string }): void => {
  webhook = options.webhook;
  enabled = true;
};

export const isReporterEnabled = (): boolean => enabled;

export const setDryRun = (on: boolean): void => {
  dryRun = on;
};

export const enqueue = (event: AggregatedEvent): void => {
  if (!enabled) return;
  if (sessionReported >= MONITOR_CONFIG.SESSION_REPORT_LIMIT) return;
  queue.push(event);
};

/**
 * 取出一批待发事件并渲染成请求体；没有可发内容时返回 null。
 * 取不到令牌时把队列整体计入 suppressed 后清空，下一条发得出去的消息会带上抑制数。
 */
const drain = (now: number): string | null => {
  if (!enabled) return null;
  if (queue.length === 0 && suppressed === 0) return null;
  if (sessionReported >= MONITOR_CONFIG.SESSION_REPORT_LIMIT) {
    queue = [];
    return null;
  }
  if (!takeToken(now)) {
    suppressed += queue.length;
    queue = [];
    return null;
  }
  const batch = queue.slice(0, MONITOR_CONFIG.MAX_EVENTS_PER_MESSAGE);
  /** 单批溢出的部分同样计入抑制，不能悄悄丢掉 */
  suppressed += queue.length - batch.length;
  queue = [];
  sessionReported += 1;
  const body = JSON.stringify(formatDingtalk(batch, suppressed, buildContext()));
  suppressed = 0;
  return body;
};

const send = (body: string): void => {
  if (dryRun) {
    console.info('[monitor] dry-run payload:', JSON.parse(body));
    return;
  }
  fetch(webhook, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => void 0);
};

export const flush = (now = Date.now()): void => {
  /** 有意例外：上报链路自身出错必须静默降级，不能影响业务页面 */
  try {
    const body = drain(now);
    if (body) send(body);
  } catch {
    /** 静默降级 */
  }
};

/** 页面卸载：fetch 可能来不及，改用 sendBeacon */
export const flushBeacon = (now = Date.now()): void => {
  /** 有意例外：同 flush */
  try {
    const body = drain(now);
    if (!body) return;
    if (dryRun) {
      console.info('[monitor] dry-run beacon:', JSON.parse(body));
      return;
    }
    const blob = new Blob([body], { type: 'application/json' });
    if (!navigator.sendBeacon?.(webhook, blob)) send(body);
  } catch {
    /** 静默降级 */
  }
};
